/**
 * walletStoryMapping.js — the jar-history and payout-chapter payloads become screen data here (#583/#584 N1
 * slice S14; design D5, D8, T15).
 *
 * The same rules as `walletMapping.js`, with its readers: money is an exact two-decimal STRING from a stored
 * settled row, each row keeps its OWN currency, and anything this cannot read makes the whole view `null`,
 * which the screen shows as a failed read, never as an empty jar. No arithmetic invents an amount here: the
 * only sums are CHECKS that a chapter's stored parts add up to its stored total.
 */
import { cents, readCurrency, readMoney, sameMember, say } from './walletMapping.js';

const JARS = Object.freeze([['save', 'Save'], ['share', 'Share'], ['spend', 'Spend']]);
const FUNDED = 'Funding target reached';

/** An instant, as the family-local calendar day of the zone the payout itself stored. */
function dayLabel(instant, timeZone) {
  const at = typeof instant === 'string' ? new Date(instant) : null;
  if (!at || Number.isNaN(at.getTime()) || typeof timeZone !== 'string' || !timeZone) return null;
  try {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone }).format(at);
  } catch {
    return null;
  }
}

/** Strict money: a two-decimal string only. A bare number is not what the server sends. */
const readText = (value) => (typeof value === 'string' ? readMoney(value) : null);

function toSavings(goal) {
  if (goal === null) return null;
  if (!goal || typeof goal !== 'object') return undefined;
  const target = readText(goal.target);
  const saved = readText(goal.saved);
  const currency = readCurrency(goal.currency);
  if (typeof goal.label !== 'string' || !goal.label || target === null || saved === null || currency === null
      || typeof goal.reached !== 'boolean' || goal.reached !== (goal.status === FUNDED)) return undefined;
  // Save money settled in another currency: the candle does not add it in, and the panel says how much.
  if (!Array.isArray(goal.notCounted)) return undefined;
  const notCounted = [];
  for (const n of goal.notCounted) {
    const other = { currency: readCurrency(n && n.currency), saved: readText(n && n.saved) };
    if (other.currency === null || other.saved === null) return undefined;
    notCounted.push(other);
  }
  return {
    label: goal.label, target, saved, currency, reached: goal.reached, status: goal.reached ? FUNDED : null,
    note: typeof goal.versionNote === 'string' ? goal.versionNote : '',
    notCounted,
  };
}

/**
 * `GET /api/wallet/:userId/jar-history` → `{ jar, label, totals, entries, nextCursor, savings }`, or `null`.
 * `savings` is present for the Save jar only: `null` for no goal, an object otherwise.
 */
export function toJarHistory(payload, { jar, expectUserId } = {}) {
  if (!payload || typeof payload !== 'object' || payload.jar !== jar) return null;
  if (expectUserId !== undefined && !sameMember(payload.userId, expectUserId)) return null;
  if (typeof payload.label !== 'string' || !Array.isArray(payload.totals) || !Array.isArray(payload.entries)) return null;

  const totals = [];
  for (const t of payload.totals) {
    const currency = readCurrency(t && t.currency);
    const allocated = readText(t && t.allocated);
    if (currency === null || allocated === null || !Number.isInteger(t.count)) return null;
    totals.push({ currency, allocated, count: t.count });
  }
  const entries = [];
  for (const e of payload.entries) {
    const currency = readCurrency(e && e.currency);
    const amount = readText(e && e.amount);
    const when = e ? dayLabel(e.settledAt, e.periodTimezone) : null;
    if (currency === null || amount === null || when === null || !Number.isInteger(e.itemId)) return null;
    entries.push({ id: e.itemId, payoutId: e.payoutId, amount, currency, when, adjustment: e.payoutKind === 'adjustment' });
  }
  const view = { jar, label: payload.label, totals, entries,
    nextCursor: typeof payload.nextCursor === 'string' && payload.nextCursor ? payload.nextCursor : null };
  if (jar === 'save') {
    view.savings = toSavings(payload.savingsGoal);
    if (view.savings === undefined) return null;
  }
  return view;
}

/**
 * The one amount a jar card may show: the whole jar's allocated total in ONE currency, `0` before any settled
 * payday, and `null` (no amount) when the jar holds more than one currency or could not be read.
 */
export function allocatedFor(view, walletCurrency) {
  if (!view) return null;
  if (view.totals.length === 0) return { amount: 0, currency: walletCurrency };
  if (view.totals.length > 1) return null;
  return { amount: view.totals[0].allocated, currency: view.totals[0].currency };
}

function readJars(jars) {
  if (!jars || typeof jars !== 'object') return null;
  const out = [];
  for (const [key, name] of JARS) {
    const amount = readText(jars[key]);
    if (amount === null) return null;
    out.push({ name, amount });
  }
  return out;
}
const sumCents = (list) => list.reduce((n, x) => n + cents(x.amount), 0);

/** `GET /api/wallet/:userId/chapters/:payoutId` → the stored receipt, or `null` when its parts do not agree. */
export function toChapterDetail(payload, { expectUserId, payoutId } = {}) {
  if (!payload || typeof payload !== 'object' || !payload.payout || typeof payload.payout !== 'object') return null;
  if (expectUserId !== undefined && !sameMember(payload.userId, expectUserId)) return null;
  const p = payload.payout;
  if (payoutId !== undefined && String(p.id) !== String(payoutId)) return null;
  const currency = readCurrency(p.currency);
  const total = readText(payload.total);
  const jars = readJars(payload.jars);
  const settled = dayLabel(p.settledAt, p.periodTimezone);
  if (currency === null || total === null || jars === null || settled === null) return null;
  if (sumCents(jars) !== cents(total) || !Array.isArray(payload.items) || payload.itemCount !== payload.items.length) return null;

  const items = [];
  for (const i of payload.items) {
    const amount = readText(i && i.amount);
    const parts = i ? readJars(i.jars) : null;
    const when = i ? dayLabel(i.completedAt, p.periodTimezone) : null;
    if (amount === null || parts === null || when === null || sumCents(parts) !== cents(amount)) return null;
    items.push({ id: i.id, amount, jars: parts, when, correction: i.correctsItemId !== null && i.correctsItemId !== undefined });
  }
  if (sumCents(items) !== cents(total)) return null;
  if (jars.some((jar, index) => items.reduce((sum, item) => sum + cents(item.jars[index].amount), 0) !== cents(jar.amount))) return null;

  if (!Array.isArray(payload.periodBatches)) return null;
  const batches = [];
  for (const b of payload.periodBatches) {
    const amount = readText(b && b.total);
    const batchCurrency = readCurrency(b && b.currency);
    if (amount === null || batchCurrency === null) return null;
    batches.push({ payoutId: b.payoutId, currency: batchCurrency, total: amount, current: String(b.payoutId) === String(p.id),
      adjustment: b.kind === 'adjustment' });
  }
  return {
    id: p.id, adjustment: p.kind === 'adjustment', currency, total, jars, items, batches, settled,
    say: (amount) => say(amount, currency),
  };
}
